// src/components/admin/UserForm.js
import React, { useState, useEffect } from 'react';
// +++ Se importan los componentes y los iconos necesarios para la nueva funcionalidad +++
import { Modal, Form, Input, Select, Switch, message, Button, Tooltip, Space } from 'antd';
import { KeyOutlined, CopyOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { adminService } from '../../services/api';

const { Option } = Select;

// +++ INICIO: Función para generar contraseñas seguras +++
const generateSecurePassword = () => {
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lower = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '@$!%*#?&_-';
    
    // Se asegura que la contraseña contenga al menos un carácter de cada tipo requerido
    let password = '';
    password += upper[Math.floor(Math.random() * upper.length)];
    password += lower[Math.floor(Math.random() * lower.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];

    // Se ajusta la longitud de la contraseña a 10 caracteres
    const allChars = upper + lower + numbers + symbols;
    for (let i = password.length; i < 10; i++) {
        password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    // Se baraja la contraseña para que los caracteres requeridos no estén siempre al principio
    return password.split('').sort(() => 0.5 - Math.random()).join('');
};
// +++ FIN: Función para generar contraseñas seguras +++

const UserForm = ({ open, onCancel, onFinish, initialValues, submitting }) => {
    const [form] = Form.useForm();
    const [roles, setRoles] = useState([]);
    const isEditing = !!initialValues;
    // +++ Se usa Form.useWatch para observar el valor del campo de contraseña en tiempo real +++
    const passwordValue = Form.useWatch('password', form);

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const { data } = await adminService.getRoles();
                if (data.success) {
                    setRoles(data.data);
                }
            } catch (error) {
                console.error('No se pudieron cargar los roles.');
            }
        };
        if (open) {
            fetchRoles();
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            form.resetFields();
            if (isEditing) {
                form.setFieldsValue({
                    ...initialValues,
                    role: initialValues.role?._id,
                });
            } else {
                form.setFieldsValue({ isActive: true });
            }
        }
    }, [open, initialValues, form, isEditing]);


    const handleOk = () => {
        form.validateFields()
            .then(values => {
                onFinish(values);
            })
            .catch(info => {
                message.warning('Por favor, complete todos los campos requeridos.');
                console.log('Validate Failed:', info);
            });
    };

    // +++ INICIO: Lógica para el generador y copiado de contraseñas +++
    const handleGeneratePassword = () => {
        const newPassword = generateSecurePassword();
        form.setFieldsValue({
            password: newPassword,
            confirm: newPassword,
        });
        message.info('Nueva contraseña generada.');
    };

    const handleCopyPassword = () => {
        const passwordToCopy = form.getFieldValue('password');
        if (passwordToCopy) {
            navigator.clipboard.writeText(passwordToCopy);
            message.success('Contraseña copiada al portapapeles.');
        }
    };
    // +++ FIN: Lógica para el generador y copiado de contraseñas +++

    return (
        <Modal
            title={isEditing ? 'Editar Usuario' : 'Crear Usuario'}
            open={open}
            onOk={handleOk}
            onCancel={onCancel}
            okButtonProps={{ loading: submitting }}
            okText={isEditing ? 'Guardar Cambios' : 'Crear'}
            cancelText="Cancelar"
            destroyOnClose
        >
            <Form form={form} layout="vertical" name="user_form">
                <Form.Item
                    name="username"
                    label="Nombre de Usuario"
                    rules={[{ required: true, message: 'Por favor, ingrese un nombre de usuario.' }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item
                    name="fullName"
                    label="Nombre Completo"
                    rules={[{ required: true, message: 'Por favor, ingrese el nombre completo.' }]}
                >
                    <Input />
                </Form.Item>
                
                {!isEditing && (
                    <>
                        {/* +++ INICIO: Campo de contraseña mejorado con generador y botón de copiar +++ */}
                        <Form.Item
                            label="Contraseña"
                            required
                        >
                            <Space.Compact style={{ width: '100%' }}>
                                <Form.Item
                                    name="password"
                                    noStyle
                                    rules={[
                                        { required: true, message: 'La contraseña es obligatoria.' },
                                        { min: 6, message: 'La contraseña debe tener al menos 6 caracteres.' },
                                        { 
                                            pattern: /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&_-])[A-Za-z\d@$!%*#?&_-]{6,}$/,
                                            message: 'Debe incluir letras, números y al menos un carácter especial.'
                                        }
                                    ]}
                                >
                                    <Input.Password 
                                        placeholder="Ingrese o genere una contraseña"
                                        iconRender={visible => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                                    />
                                </Form.Item>
                                <Tooltip title="Generar Contraseña Segura">
                                    <Button icon={<KeyOutlined />} onClick={handleGeneratePassword} />
                                </Tooltip>
                                <Tooltip title="Copiar Contraseña">
                                    <Button icon={<CopyOutlined />} onClick={handleCopyPassword} disabled={!passwordValue} />
                                </Tooltip>
                            </Space.Compact>
                        </Form.Item>

                        <Form.Item
                            name="confirm"
                            label="Confirmar Contraseña"
                            dependencies={['password']}
                            hasFeedback
                            rules={[
                                { required: true, message: 'Por favor, confirme la contraseña.' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('Las contraseñas no coinciden.'));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password 
                                placeholder="Confirme la contraseña"
                                iconRender={visible => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                            />
                        </Form.Item>
                        {/* +++ FIN: Campo de contraseña mejorado con generador y botón de copiar +++ */}
                    </>
                )}
                
                <Form.Item
                    name="role"
                    label="Rol"
                    rules={[{ required: true, message: 'Por favor, seleccione un rol.' }]}
                >
                    <Select placeholder="Seleccione un rol">
                        {roles.map(role => (
                            <Option key={role._id} value={role._id}>
                                {role.name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item name="isActive" label="Estado" valuePropName="checked">
                    <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default UserForm;
